import { LitElement } from 'lit';
import { customElement, query } from 'lit/decorators.js';
import { SCROLL_LOCK } from '@constants/classes';
import { debounce } from '@kluntje/js-utils/lib/function-helpers/decorators';
import { addClass, removeClass, hasClass, toggleClass } from '@kluntje/js-utils/lib/dom-helpers/index.js';

@customElement('man-main-nav')
export class ManMainNav extends LitElement {
  @query('.man-main-nav__toggle')
  toggleButton!: HTMLButtonElement;

  parentHeader!: HTMLElement | null;
  subNavButtons!: NodeListOf<Element>;
  subNavDesktop!: HTMLElement | null;
  subNavDesktopAside!: HTMLElement | null;
  subNavDesktopAsideItems!: NodeListOf<Element>;
  subNavDesktopBody!: HTMLElement | null;
  subNavDesktopTitle!: HTMLElement | null;
  subNavDismissButton!: Element | null | undefined;
  subNavs!: NodeListOf<Element>;
  firstLevelItemsMinWidth = 425;

  protected createRenderRoot(): HTMLElement | DocumentFragment {
    return this;
  }

  resetStyles() {
    this.subNavButtons.forEach((button) => {
      removeClass(button, 'active');
    });

    this.subNavs.forEach((subNav) => {
      removeClass(subNav, 'man-sub-nav--open');
    });

    if (this.subNavDesktopBody) {
      this.subNavDesktopBody.innerHTML = '';
    }

    removeClass(this.subNavDesktop, 'man-sub-nav-desktop--open');
    removeClass(this.parentHeader, 'header--main-nav-active');
  }

  onClickSubNavButton = (evt: Event) => {
    const currentTarget = evt.currentTarget as HTMLElement;
    const currentParent = currentTarget.parentElement;
    const currentSubNav = currentParent?.querySelector('[data-js-sub-nav]') as HTMLElement;

    const siblings = currentTarget.closest('li')?.parentElement?.children;

    // desktop nav
    if (hasClass(this, 'man-main-nav--desktop')) {
      if (hasClass(currentTarget, 'active')) return;

      this.subNavButtons.forEach((button) => {
        removeClass(button, 'active');
      });

      addClass(currentTarget, 'active');
      removeClass(currentParent, 'man-sub-nav--hidden');

      if (this.subNavDesktop && this.subNavDesktopAside && this.subNavDesktopBody && currentSubNav) {
        toggleClass(currentSubNav, 'man-sub-nav--open');

        this.renderDesktopSubNav(currentSubNav);
        this.attachedDesktopEventListeners();

        addClass(this.subNavDesktop, 'man-sub-nav-desktop--open');
      }

      return;
    }

    if (siblings) {
      [...siblings].forEach((item) => {
        addClass(item, 'man-sub-nav--hidden');
      });
    }

    // mobile nav
    if (hasClass(currentTarget, 'active')) {
      removeClass(currentTarget, 'active');
      removeClass(currentSubNav, 'man-sub-nav--open');

      if (siblings) {
        [...siblings].forEach((item) => {
          removeClass(item, 'man-sub-nav--hidden');
        });
      }
    } else {
      toggleClass(currentTarget, 'active');
      removeClass(currentParent, 'man-sub-nav--hidden');
      toggleClass(currentSubNav, 'man-sub-nav--open');
    }
  };

  onClickDesktopAsideItem = (evt: Event) => {
    const currentTarget = evt.currentTarget as HTMLElement;
    const currentSubNav = currentTarget?.querySelector('[data-js-sub-nav]') as Node;
    const currentSubNavButton = currentTarget?.querySelector('[data-js-sub-nav-button]') as HTMLElement;
    const currentAsideSubNavButtons = this.subNavDesktopAside?.querySelectorAll('[data-js-sub-nav-button]');

    currentAsideSubNavButtons?.forEach((button) => {
      removeClass(button, 'active');
    });

    addClass(currentSubNavButton, 'active');

    if (this.subNavDesktopBody && currentSubNav) {
      this.subNavDesktopBody.innerHTML = '';
      this.subNavDesktopBody.append(currentSubNav.cloneNode(true));
    }
  };

  renderDesktopSubNav = (subNav: Element) => {
    if (
      !(this.subNavDesktop && this.subNavDesktopAside && this.subNavDesktopBody && this.subNavDesktopTitle && subNav)
    ) {
      return;
    }

    this.subNavDesktopTitle.innerHTML = '';
    this.subNavDesktopAside.innerHTML = '';
    this.subNavDesktopBody.innerHTML = '';

    const title = subNav.querySelector('.man-sub-nav__title');

    if (title) {
      this.subNavDesktopTitle.append(title.cloneNode(true));
    }

    if (hasClass(subNav, 'man-sub-nav--link-list')) {
      removeClass(this.subNavDesktopAside, 'col-3');
      addClass(this.subNavDesktopBody, 'col-12');
      removeClass(this.subNavDesktopBody, 'col-9');

      this.subNavDesktopBody.append(subNav.cloneNode(true));
    }

    if (hasClass(subNav, 'man-sub-nav--teaser-list')) {
      addClass(this.subNavDesktopAside, 'col-3');
      removeClass(this.subNavDesktopBody, 'col-12');
      addClass(this.subNavDesktopBody, 'col-9');

      const firstUL = subNav.querySelector('.man-sub-nav-list');
      const listItems = firstUL?.children;

      if (listItems) {
        const asideUL = document.createElement('ul');

        [...listItems].forEach((item) => {
          const clonedItem = item.cloneNode(true) as HTMLElement;

          removeClass(clonedItem, 'col-xl-4');

          asideUL?.append(clonedItem);
        });

        this.subNavDesktopAside.append(asideUL);
        this.subNavDesktopAsideItems = this.subNavDesktopAside.querySelectorAll('li');
      }

      const firstSubNav = subNav.querySelector('[data-js-sub-nav]');
      const asideNav = document.querySelector('.man-sub-nav-desktop__aside');
      const asideNavFirstLink = asideNav?.querySelector('.man-main-nav-item__link');

      if (firstSubNav && asideNav) {
        asideNavFirstLink?.classList.add('active');

        this.subNavDesktopBody.append(firstSubNav.cloneNode(true));
      }
    }
  };

  attachedDesktopEventListeners = () => {
    this.subNavDesktopAsideItems?.forEach((item) => {
      item.addEventListener('click', this.onClickDesktopAsideItem);
    });

    // because cloneNode(...) does not copy event listeners
    this.attachDataLayerEventListeners();

    this.subNavDismissButton?.removeEventListener('click', this.onClickDismiss);
    this.subNavDismissButton = this.subNavDesktop?.querySelector('[data-dismiss]');
    this.subNavDismissButton?.addEventListener('click', this.onClickDismiss);
  };

  private attachDataLayerEventListeners() {
    const dataLayerEnabled = document.body.hasAttribute('data-cmp-data-layer-enabled');

    if (dataLayerEnabled) {
      const dataLayerName = document.body.getAttribute('data-cmp-data-layer-name') ?? 'adobeDataLayer';
      // @ts-expect-error type of window does not contain the attribute
      const dataLayer: Array<unknown> = window[dataLayerName] ?? [];

      this.subNavDesktop?.querySelectorAll<HTMLElement>('[data-cmp-clickable]').forEach((elem) => {
        elem.addEventListener('click', () => {
          dataLayer.push({
            event: 'cmp:click',
            eventInfo: {
              path: 'component.' + getDataLayerId(elem),
            },
          });
        });
      });
    }

    function getDataLayerId(item: HTMLElement): string | null {
      if (item.dataset.cmpDataLayer) {
        return Object.keys(JSON.parse(item.dataset.cmpDataLayer))[0];
      } else if (item.parentElement?.dataset.cmpDataLayer) {
        return Object.keys(JSON.parse(item.parentElement.dataset.cmpDataLayer))[0];
      } else {
        return item.id ?? item.parentElement?.id;
      }
    }
  }

  private onClickToggleNav = () => {
    toggleClass(document.body, SCROLL_LOCK);
    toggleClass(this, 'active');
    toggleClass(this.parentHeader, 'header--main-nav-active', hasClass(this, 'active'));

    if (hasClass(this, 'active')) {
      window.scrollTo(0, 0);
    }
  };

  private onClickDocument = (event: MouseEvent) => {
    const target = event.target as Node;
    const toggle = this.shadowRoot?.querySelector('.man-main-nav__toggle');
    const isClickInsideMain = this.contains(event.target as Node) || toggle?.contains(event.target as Node);

    const isClickInsideDesktopNav =
      this.subNavDesktop?.contains(event.target as Node) ||
      this.subNavDesktopAside?.contains(event.target as Node) ||
      this.subNavDesktopBody?.contains(event.target as Node);

    const isClickOnSubNavButton = Array.from(this.subNavButtons ?? []).some((btn) => btn.contains(target));

    // Mobile NAV: Close if clicking outside
    if (!isClickInsideMain && hasClass(document.body, SCROLL_LOCK)) {
      removeClass(document.body, SCROLL_LOCK);
      removeClass(this, 'active');
      removeClass(this.parentHeader, 'header--main-nav-active');
    }

    // Desktop NAV: Close if clicking outside
    if (
      !isClickInsideDesktopNav &&
      !isClickOnSubNavButton &&
      this.subNavDesktop?.classList.contains('man-sub-nav-desktop--open')
    ) {
      this.onClickDismiss();
    }
  };

  onClickDismiss = () => {
    this.resetStyles();
    this.subNavDismissButton?.removeEventListener('click', this.onClickDismiss);
    this.subNavDesktopAsideItems?.forEach((item) => {
      item.removeEventListener('click', this.onClickDesktopAsideItem);
    });
  };

  firstUpdated() {
    this.toggleButton?.addEventListener('click', this.onClickToggleNav);

    this.parentHeader = this.closest('header');

    this.subNavs = this.querySelectorAll('[data-js-sub-nav]');
    this.subNavButtons = this.querySelectorAll('[data-js-sub-nav-button]');
    this.subNavDesktop = document.querySelector('[data-js-man-sub-nav-desktop]');
    this.subNavDesktopTitle = document.querySelector('[data-js-man-sub-nav-desktop-title]');
    this.subNavDesktopAside = document.querySelector('[data-js-man-sub-nav-desktop-aside]');
    this.subNavDesktopBody = document.querySelector('[data-js-man-sub-nav-desktop-body]');

    this.subNavButtons?.forEach((button) => {
      button.addEventListener('click', this.onClickSubNavButton);
    });

    document.addEventListener('click', this.onClickDocument);

    this.initOnResize();
    this.onResize();
  }

  initOnResize = () => {
    const firstLevelItems = this.querySelectorAll('.man-main-nav__items > .man-main-nav-item');

    const firstLevelItemsMinWidth = [...firstLevelItems]
      .map((item) => {
        const linkLabel = item.querySelector('.man-main-nav-item__link')?.textContent;

        return linkLabel?.trim().length || 0;
      })
      .reduce((prev, next) => {
        const extraSpace = 24; // padding and margin of a link item
        const letterSpacing = 8.15; // estimate of the width of any letter

        return prev + letterSpacing * next + extraSpace;
      }, 0);

    this.firstLevelItemsMinWidth = Math.max(this.firstLevelItemsMinWidth, firstLevelItemsMinWidth);

    window.addEventListener('resize', this.onResize.bind(this));
  };

  @debounce(30)
  onResize() {
    const headerContent = document.querySelector('.header__content');
    const desktopLogoWidth = 152;

    if (!headerContent) {
      return;
    }

    const availableMainNavWidth = headerContent?.clientWidth - desktopLogoWidth;

    if (availableMainNavWidth > this.firstLevelItemsMinWidth) {
      addClass(this, 'man-main-nav--desktop');
      addClass(this.parentHeader, 'header--desktop');

      if (this.classList.contains('active')) {
        removeClass(document.body, SCROLL_LOCK);
      }
    } else {
      removeClass(this, 'man-main-nav--desktop');
      removeClass(this.parentHeader, 'header--desktop');

      if (this.classList.contains('active')) {
        addClass(document.body, SCROLL_LOCK);
      }
    }
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    this.toggleButton?.removeEventListener('click', this.onClickToggleNav);

    this.subNavDismissButton?.removeEventListener('click', this.onClickDismiss);
    this.subNavButtons.forEach((button) => {
      button.removeEventListener('click', this.onClickSubNavButton);
    });

    document.removeEventListener('click', this.onClickDocument);

    window.removeEventListener('resize', this.onResize);
  }
}
